import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { sieveAnalysis as api, intakes as intakesApi, boreholes as boreholesApi } from '@/api/endpoints';
import type { Intake, SieveProject, SieveProjectInput, SieveProjectStatus } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

/** Etykiety statusów — te same, co w filtrach listy i w enumie SieveProjectStatus po stronie backendu. */
const STATUS_OPTIONS: { value: SieveProjectStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Szkic' },
  { value: 'ACTIVE', label: 'W analizie' },
  { value: 'DONE', label: 'Zakończone' },
];

/** Wiersz listy otworów ujęcia — kształt odpowiedzi boreholes.listForIntake(). */
interface BoreholeOption { id: number; designation: string; function: string }

/**
 * Mapuje projekt na komplet pól wejściowych PUT-a. Wydzielone, bo backend nadpisuje WSZYSTKIE
 * pola projektu — każdy ekran, który zapisuje choćby jedno pole (np. podpis w zakładce raportu),
 * musi wysłać resztę bez zmian, inaczej je wyzeruje.
 */
export function sieveProjectToInput(p: SieveProject): SieveProjectInput {
  return {
    name: p.name,
    docNo: p.docNo ?? null,
    intakeId: p.intakeId ?? null,
    boreholeId: p.boreholeId ?? null,
    clientId: p.clientId ?? null,
    intakeName: p.intakeName ?? null,
    town: p.town ?? null,
    gmina: p.gmina ?? null,
    powiat: p.powiat ?? null,
    woj: p.woj ?? null,
    well: p.well ?? null,
    studyDate: p.studyDate ?? null,
    performedBy: p.performedBy ?? null,
    geologName: p.geologName ?? null,
    geologLic: p.geologLic ?? null,
    reportDate: p.reportDate ?? null,
    dvgwIntermittent: p.dvgwIntermittent,
    status: p.status,
    notes: p.notes ?? null,
  };
}

interface Props {
  /** Brak projektu = tryb tworzenia (POST). Przekazany projekt = tryb edycji (PUT). */
  project?: SieveProject | null;
  onClose: () => void;
  /** Zapisany projekt (świeży z backendu) — wołający decyduje, czy przeładować listę, czy przejść do karty. */
  onSaved: (project: SieveProject) => void;
}

/**
 * Wspólny formularz projektu analizy sitowej — tworzenie i edycja.
 *
 * Uwaga na PUT: backend (SieveProjectService.update) nadpisuje KOMPLET pól projektu, więc pole
 * pominięte w żądaniu zostałoby wyzerowane. Dlatego payload budujemy zawsze w całości, łącznie
 * z polami, których formularz nie pokazuje wprost (clientId — powiązanie z klientem ustawiane
 * przy tworzeniu z ujęcia; bez przepisania go PUT skasowałby powiązanie).
 */
export function SieveProjectFormModal({ project, onClose, onSaved }: Props) {
  const editing = !!project;

  const [name, setName] = useState(project?.name ?? '');
  const [docNo, setDocNo] = useState(project?.docNo ?? '');
  const [intakeId, setIntakeId] = useState<number | ''>(project?.intakeId ?? '');
  const [boreholeId, setBoreholeId] = useState<number | ''>(project?.boreholeId ?? '');
  const [intakeName, setIntakeName] = useState(project?.intakeName ?? '');
  const [well, setWell] = useState(project?.well ?? '');
  const [town, setTown] = useState(project?.town ?? '');
  const [gmina, setGmina] = useState(project?.gmina ?? '');
  const [powiat, setPowiat] = useState(project?.powiat ?? '');
  const [woj, setWoj] = useState(project?.woj ?? '');
  const [studyDate, setStudyDate] = useState(project?.studyDate ?? '');
  const [performedBy, setPerformedBy] = useState(project?.performedBy ?? '');
  const [geologName, setGeologName] = useState(project?.geologName ?? '');
  const [geologLic, setGeologLic] = useState(project?.geologLic ?? '');
  const [reportDate, setReportDate] = useState(project?.reportDate ?? '');
  const [dvgwIntermittent, setDvgwIntermittent] = useState(project?.dvgwIntermittent ?? false);
  const [status, setStatus] = useState<SieveProjectStatus>(project?.status ?? 'ACTIVE');
  const [notes, setNotes] = useState(project?.notes ?? '');

  // Powiązanie z klientem nie ma własnej kontrolki (ustawia je prefill z ujęcia przy tworzeniu),
  // ale musi przejść przez PUT nietknięte — inaczej edycja projektu zerwałaby to powiązanie.
  const clientId = project?.clientId ?? null;

  const [intakeList, setIntakeList] = useState<Intake[]>([]);
  const [boreholeList, setBoreholeList] = useState<BoreholeOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { intakesApi.list().then(setIntakeList).catch(() => {}); }, []);

  // Otwory istnieją tylko w kontekście ujęcia — bez wybranego ujęcia nie ma czego pobierać.
  useEffect(() => {
    if (intakeId === '') { setBoreholeList([]); return; }
    let aktualne = true;
    boreholesApi.listForIntake(Number(intakeId))
      .then(list => { if (aktualne) setBoreholeList(list); })
      .catch(() => { if (aktualne) setBoreholeList([]); });
    return () => { aktualne = false; };
  }, [intakeId]);

  const pickIntake = (v: string) => {
    const id = v ? Number(v) : '';
    setIntakeId(id);
    // Otwór należy do konkretnego ujęcia — po zmianie ujęcia poprzedni wybór jest już nieprawdziwy.
    setBoreholeId('');
    const it = intakeList.find(x => x.id === id);
    if (it) {
      // Prefill tylko pustych pól — ręcznie wpisanej lokalizacji nie nadpisujemy.
      if (!intakeName) setIntakeName(it.name || it.number);
      if (!town && it.town) setTown(it.town);
      if (!gmina && it.gmina) setGmina(it.gmina);
      if (!powiat && it.county) setPowiat(it.county);
      if (!woj && it.voivodeship) setWoj(it.voivodeship);
    }
  };

  const pickBorehole = (v: string) => {
    const id = v ? Number(v) : '';
    setBoreholeId(id);
    // Prefill tylko pustego pola tekstowego — ręczne „2A (rekonstrukcja)" ma prawo zostać.
    const bh = boreholeList.find(x => x.id === id);
    if (bh && !well.trim()) setWell(bh.designation);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Nazwa opracowania jest wymagana'); return; }
    setBusy(true); setErr(null);
    const payload: SieveProjectInput = {
      name: name.trim(),
      docNo: docNo.trim() || null,
      intakeId: intakeId === '' ? null : Number(intakeId),
      boreholeId: boreholeId === '' ? null : Number(boreholeId),
      clientId,
      intakeName: intakeName.trim() || null,
      town: town.trim() || null,
      gmina: gmina.trim() || null,
      powiat: powiat.trim() || null,
      woj: woj.trim() || null,
      well: well.trim() || null,
      studyDate: studyDate || null,
      performedBy: performedBy.trim() || null,
      geologName: geologName.trim() || null,
      geologLic: geologLic.trim() || null,
      reportDate: reportDate || null,
      dvgwIntermittent,
      status,
      notes: notes.trim() || null,
    };
    try {
      const saved = project
        ? await api.update(project.id, payload)
        : await api.create(payload);
      onSaved(saved);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { name, docNo, intakeId, boreholeId, intakeName, well, town, gmina, powiat, woj, studyDate, performedBy, geologName, geologLic, reportDate, dvgwIntermittent, status, notes }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[720px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900">
            {editing ? 'Edycja projektu analizy sitowej' : 'Nowy projekt analizy sitowej'}
          </h2>
        </div>
        <div className="p-5 grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Nazwa opracowania *</span>
              <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="np. REDA 14C" />
            </label>
            <label className="field">
              <span className="field-label">Opracowanie nr</span>
              <input value={docNo} onChange={e => setDocNo(e.target.value)} className="input" placeholder="np. GF/2026/014" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Ujęcie (powiązanie z portalem — opcjonalnie)</span>
              <SearchableSelect value={intakeId === '' ? '' : String(intakeId)} onChange={pickIntake}>
                <option value="">— brak powiązania —</option>
                {intakeList.map(it => <option key={it.id} value={it.id}>{it.name} ({it.number})</option>)}
              </SearchableSelect>
            </label>

            {/* Powiązanie z otworem (V164). Otwory są listowane w obrębie ujęcia, więc pole
                ożywa dopiero po wskazaniu ujęcia; zmiana ujęcia czyści wybór. */}
            <label className="field">
              <span className="field-label">Otwór (powiązanie z kartą otworu — opcjonalnie)</span>
              <SearchableSelect value={boreholeId === '' ? '' : String(boreholeId)}
                                onChange={pickBorehole}
                                disabled={intakeId === ''}>
                <option value="">— brak powiązania —</option>
                {boreholeList.map(b => (
                  <option key={b.id} value={b.id}>{b.designation}{b.function ? ` — ${b.function}` : ''}</option>
                ))}
              </SearchableSelect>
              {intakeId === '' && (
                <span className="field-hint">Najpierw wskaż ujęcie — otwory wybiera się z jego listy.</span>
              )}
              {intakeId !== '' && boreholeList.length === 0 && (
                <span className="field-hint">To ujęcie nie ma jeszcze wpisanych otworów.</span>
              )}
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Ujęcie (nazwa)</span>
              <input value={intakeName} onChange={e => setIntakeName(e.target.value)} className="input" />
            </label>
            <label className="field">
              <span className="field-label">Otwór</span>
              <input value={well} onChange={e => setWell(e.target.value)} className="input" placeholder="np. 14C" />
            </label>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <label className="field"><span className="field-label">Miejscowość</span>
              <input value={town} onChange={e => setTown(e.target.value)} className="input" /></label>
            <label className="field"><span className="field-label">Gmina</span>
              <input value={gmina} onChange={e => setGmina(e.target.value)} className="input" /></label>
            <label className="field"><span className="field-label">Powiat</span>
              <input value={powiat} onChange={e => setPowiat(e.target.value)} className="input" /></label>
            <label className="field"><span className="field-label">Województwo</span>
              <input value={woj} onChange={e => setWoj(e.target.value)} className="input" /></label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Data badania</span>
              <input type="date" value={studyDate} onChange={e => setStudyDate(e.target.value)} className="input" /></label>
            <label className="field"><span className="field-label">Wykonał</span>
              <input value={performedBy} onChange={e => setPerformedBy(e.target.value)} className="input" /></label>
            <label className="field"><span className="field-label">Geolog (podpis)</span>
              <input value={geologName} onChange={e => setGeologName(e.target.value)} className="input" /></label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Nr uprawnień geologicznych</span>
              <input value={geologLic} onChange={e => setGeologLic(e.target.value)} className="input" /></label>
            <label className="field"><span className="field-label">Data raportu</span>
              <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} className="input" /></label>
            <label className="field"><span className="field-label">Status</span>
              <SearchableSelect value={status} onChange={v => setStatus(v as SieveProjectStatus)}
                                searchThreshold={20}
                                options={STATUS_OPTIONS.map(s => ({ value: s.value, label: s.label }))} />
            </label>
          </div>

          {/* Przełącznik wprost zmienia Fg w metodzie DVGW (5 przy pracy przerywanej, 5 + CU/10 przy ciągłej),
              więc jego zmiana przelicza dobór obsypki dla WSZYSTKICH próbek projektu. */}
          <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer">
            <input type="checkbox" checked={dvgwIntermittent} onChange={e => setDvgwIntermittent(e.target.checked)}
                   className="w-4 h-4 accent-water-500" />
            DVGW: praca przerywana studni (Fg = 5)
          </label>

          <label className="field">
            <span className="field-label">Uwagi</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2} />
          </label>

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}

          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5" />
              {busy ? (editing ? 'Zapisywanie…' : 'Tworzenie…') : (editing ? 'Zapisz zmiany' : 'Utwórz projekt')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
